import React, { useEffect, useState, useCallback } from "react";
import { Terrain, TerrainChunk } from "./Terrain";
import {
    Engine,
    Scene,
    Vector3,
    Color4,
    TransformNode,
    Mesh,
    Texture,
    ShaderMaterial,
    VertexBuffer,
    Vector2
} from "@babylonjs/core";
import { resourceApi, sqlApi } from "../NejlikaApi";

interface TerrainLoaderProps {
    project: string;
    engine: Engine;
    scene: Scene;
    terrain: Terrain;
    applied?: boolean;
}

export const TerrainLoader: React.FC<TerrainLoaderProps> = ({ project, engine, scene, terrain, applied }) => {
    const [, setTerrainNode] = useState<TransformNode>();

    applied = applied ?? true;

    const createTerrainChunk = useCallback(async (chunk: TerrainChunk, scene: Scene) => {
        const { data, scale, translation, width, height } = chunk.heightmap;

        const terrainChunk = new Mesh("terrainChunk", scene);

        // Create vertex data for the terrain
        const subdivisions = width - 1; // Ensure enough vertices
        const vertexData = {
            positions: [] as number[],
            indices: [] as number[],
            normals: [] as number[],
            uvs: [] as number[],
        };

        const imageData = data;

        // Generate positions and UVs
        for (let z = 0; z <= subdivisions; z++) {
            for (let x = 0; x <= subdivisions; x++) {
                const xPos = (x / subdivisions) * width - width / 2;
                const zPos = (z / subdivisions) * height - height / 2;

                // Calculate the corresponding pixel in the image data
                const pixelX = Math.floor((x / subdivisions) * (imageData.length - 1));
                const pixelZ = Math.floor((z / subdivisions) * (imageData[0].length - 1));
                const heightValue = imageData[pixelX][pixelZ]; // Normalize height (assuming red channel)

                vertexData.positions.push(xPos, heightValue, zPos); // Set Y based on height map
                vertexData.uvs.push(x / subdivisions, z / subdivisions);
                
                // Generate indices
                if (x < subdivisions && z < subdivisions) {
                    const topLeft = z * (subdivisions + 1) + x;
                    const topRight = topLeft + 1;
                    const bottomLeft = (z + 1) * (subdivisions + 1) + x;
                    const bottomRight = bottomLeft + 1;

                    vertexData.indices.push(topLeft, topRight, bottomLeft);
                    vertexData.indices.push(topRight, bottomRight, bottomLeft);
                }
            }
        }

        // Calculate normals based on height data
        for (let z = 0; z <= subdivisions; z++) {
            for (let x = 0; x <= subdivisions; x++) {
                // Get height values for normal calculation
                const getHeight = (px: number, pz: number): number => {
                    if (px < 0 || px >= imageData.length || pz < 0 || pz >= imageData[0].length) {
                        return imageData[Math.max(0, Math.min(imageData.length - 1, px))][Math.max(0, Math.min(imageData[0].length - 1, pz))];
                    }
                    return imageData[px][pz];
                };

                const pixelX = Math.floor((x / subdivisions) * (imageData.length - 1));
                const pixelZ = Math.floor((z / subdivisions) * (imageData[0].length - 1));

                // Calculate normal using neighboring heights
                const left = getHeight(pixelX - 1, pixelZ);
                const right = getHeight(pixelX + 1, pixelZ);
                const up = getHeight(pixelX, pixelZ - 1);
                const down = getHeight(pixelX, pixelZ + 1);

                const normalX = (left - right) * 0.1; // Scale factor for normal intensity
                const normalZ = (up - down) * 0.1;
                const normalY = 1.0;

                // Normalize the normal vector
                const length = Math.sqrt(normalX * normalX + normalY * normalY + normalZ * normalZ);
                vertexData.normals.push(normalX / length, normalY / length, normalZ / length);
            }
        }

        // Load textures for the terrain blend shader
        const loadTerrainTexture = (url: string, fallbackColor: string = "white"): Texture => {
            if (!url) {
                // Create a solid color texture if no URL provided
                const canvas = document.createElement('canvas');
                canvas.width = canvas.height = 1;
                const ctx = canvas.getContext('2d')!;
                ctx.fillStyle = fallbackColor;
                ctx.fillRect(0, 0, 1, 1);
                const dataUrl = canvas.toDataURL();
                return new Texture(dataUrl, scene);
            }
            
            try {
                const texture = new Texture(url, scene);
                texture.wrapU = Texture.WRAP_ADDRESSMODE;
                texture.wrapV = Texture.WRAP_ADDRESSMODE;
                console.log(`Loaded texture: ${url}`);
                return texture;
            } catch (error) {
                console.warn(`Failed to load texture: ${url}`, error);
                return loadTerrainTexture("", fallbackColor);
            }
        };

        const pallet = chunk.texturing.pallet;

        let textureMap: Texture[] = [];

        // Load textures from the texture resources
        for (const resourceID of pallet) {
            const textureResources = await sqlApi.query(project, "SELECT texturepath FROM mapTextureResource WHERE id = " + resourceID);
            const resource = textureResources[0];
            if (typeof resource.texturepath === "string") {
                try {
                    // Replace .dds with .png for texture paths
                    const pngPath = resource.texturepath.replace(/\.dds$/, ".png");
                    const textureUrl = resourceApi.resourceUrl(project, `textures/env/${pngPath}`);
                    let texture = loadTerrainTexture(textureUrl, "#FFFFFF"); // Default to white if loading fails
                    textureMap.push(texture);
                } catch (error) {
                    console.warn(`Failed to load texture: ${resource.texturepath}`, error);
                }
            }
        }

        // Apply vertex data to the mesh
        terrainChunk.setVerticesData(VertexBuffer.PositionKind, vertexData.positions);
        terrainChunk.setVerticesData(VertexBuffer.NormalKind, vertexData.normals);
        terrainChunk.setVerticesData(VertexBuffer.UVKind, vertexData.uvs);
        terrainChunk.setIndices(vertexData.indices);

        // Position the chunk based on its translation
        terrainChunk.setPositionWithLocalVector(new Vector3(translation[1], 0, translation[0]));
        terrainChunk.scaling.set(scale, 1, scale);
        terrainChunk.rotation.y = Math.PI * 2;
        terrainChunk.alwaysSelectAsActiveMesh = true;

        // Use blend map for texture mixing weights
        const textureMapTexture = loadTerrainTexture(chunk.texturing?.path || "", "#000000"); // Black fallback for blend map

        // Color texture for overlay
        const colorTexture = loadTerrainTexture(chunk.colors?.path || "", "#FFFFFF"); // White fallback

        const blendMap = loadTerrainTexture(chunk.blendmap?.path || "", "#000000"); // Black fallback for blend map

        const lightmap = loadTerrainTexture(chunk.lightmap?.path || "", "#FFFFFF"); // White fallback for lightmap

        // Attach the texture blend shader material
        const shaderMaterial = new ShaderMaterial("terrainTextureBlendShader", scene, {
            vertex: "/shaders/terrain-textureblend",
            fragment: "/shaders/terrain-textureblend",
        }, {
            attributes: ["position", "normal", "uv"],
            uniforms: ["world", "worldViewProjection", "texture0", "texture1", "texture2", "texture3", "textureMap", "colorTexture", "color", "textureMapScale", "blendMap", "lightMap"],
        });

        // Set textures
        shaderMaterial.setTexture("texture0", textureMap[0]);
        shaderMaterial.setTexture("texture1", textureMap[1]);
        shaderMaterial.setTexture("texture2", textureMap[2]);
        shaderMaterial.setTexture("texture3", textureMap[3]);
        shaderMaterial.setTexture("textureMap", textureMapTexture);
        shaderMaterial.setTexture("colorTexture", colorTexture);
        shaderMaterial.setVector2("textureMapScale", new Vector2(1.0 / 65.0 / 3.2, -1.0 / 65.0 / 3.2));
        shaderMaterial.setTexture("blendMap", blendMap);
        shaderMaterial.setTexture("lightMap", lightmap);

        // Set base color (white tint)
        shaderMaterial.setColor4("color", new Color4(1.0, 1.0, 1.0, 1.0));

        terrainChunk.material = shaderMaterial;

        console.log("Created terrain chunk with texture blend shader:", {
            heightmap: chunk.heightmap.path,
            texturing: chunk.texturing?.path,
            colors: chunk.colors?.path,
            blendmap: chunk.blendmap?.path,
            lightmap: chunk.lightmap?.path,
            vertexCount: vertexData.positions.length / 3,
            triangleCount: vertexData.indices.length / 3
        });

        return terrainChunk;
    }, [project]);

    const createTerrain = useCallback(async (chunk: Terrain) => {
        if (!scene) return null;

        const centerX = 204.8 / 2.0;
        const centerY = 204.8 / 2.0;

        // Create a root node for the terrain
        const terrainRoot = new TransformNode("terrainRoot", scene);

        for (let terrainChunk of chunk.chunks) {
            const chunk = await createTerrainChunk(terrainChunk, scene);

            // Attach the chunk to the root node
            chunk.parent = terrainRoot;
        }

        terrainRoot.position.set(centerX, 0, centerY);
        terrainRoot.scaling.set(-1, 1, 1);
        terrainRoot.rotation.y = Math.PI / 2;

        return terrainRoot;
    }, [scene, createTerrainChunk]);

    useEffect(() => {
        if (terrain && scene) {
            createTerrain(terrain).then((node) => {
                if (!node) {
                    console.error("Failed to create terrain.");
                    return;
                }
                setTerrainNode(node);
            }).catch((err) => {
                console.error("Failed to create terrain:", err);
            });
        }
    }, [terrain, scene, createTerrain]);

    return null;
};
